/**
 * lib/format.ts — how numbers read on screen.
 *
 * Task 05, checklist item 3: «длительность форматируется, для миксов — часы:минуты:секунды».
 * Nothing here knows about React or about the server.
 */

/**
 * Task 23: a tempo as the deck and the status row print it — a sign, one decimal, a comma:
 * `2.5` → `+2,5 %`, `-7.8` → `−7,8 %`, `0` → `0,0 %`. The minus is the typographic one.
 */
export function formatPitch(pitch: number): string {
  const sign = pitch > 0 ? '+' : pitch < 0 ? '−' : '';
  return `${sign}${Math.abs(pitch).toFixed(1).replace('.', ',')} %`;
}

/**
 * Task 48: what the sleep timer has left — minutes and seconds, the minutes never turned into
 * hours: «60 мин» counts down from `60:00`, as it was asked for. `0:00` for nothing left or a
 * non-number: a timer on is never an empty cell. Task 60 writes a HOT CUE's second over its pad
 * the same way (`lib/pult-pads.ts`): the screen over a pad has room for `62:05`, not `1:02:05`,
 * and a cue at the very start is `0:00`, not nothing.
 */
export function formatCountdown(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * `213` → `3:33`, `7384` → `2:03:04`.
 *
 * Hours appear only when there are hours: a three-minute song written as `0:03:33` wastes
 * three characters of the narrowest column in the row. Minutes carry no leading zero for the
 * same reason; seconds always have two digits, or `3:3` would read as three minutes three.
 *
 * `''` for zero, a negative or a non-number. The indexer writes 0 when Telegram gave no
 * duration (a file sent with the paperclip), and `0:00` next to a track that plays for four
 * minutes is a wrong fact, where an empty cell is merely a missing one.
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 1) return '';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  if (h === 0) return `${String(m)}:${ss}`;
  return `${String(h)}:${String(m).padStart(2, '0')}:${ss}`;
}

/**
 * Russian plural of «трек»: 1 трек, 2 трека, 5 треков, 11 треков, 21 трек, 104 трека.
 *
 * The teens are the exception every naive version gets wrong: 11–14 take the «many» form
 * although they end in 1–4.
 */
export function trackCount(n: number): string {
  return countOf(n, 'трек', 'трека', 'треков');
}

/** Task 66: the same for «ролик», beside the title of «Видео»: 1 ролик, 4 ролика, 11 роликов. */
export function clipCount(n: number): string {
  return countOf(n, 'ролик', 'ролика', 'роликов');
}

/** Task 69: a riddim's versions, under its name: 1 версия, 3 версии, 5 версий. */
export function versionCount(n: number): string {
  return countOf(n, 'версия', 'версии', 'версий');
}

/**
 * Task 71: how long a queue lasts, under its title — «3 ч 41 мин», or «45 мин» under an hour, to
 * the nearest minute. Whole hours say no minutes: «2 ч».
 */
export function formatSpan(seconds: number): string {
  const minutes = Math.round(Math.max(0, seconds) / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${String(rest)} мин`;
  return rest === 0 ? `${String(hours)} ч` : `${String(hours)} ч ${String(rest)} мин`;
}

function countOf(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  let word = many;
  if (mod100 < 11 || mod100 > 14) {
    if (mod10 === 1) word = one;
    else if (mod10 >= 2 && mod10 <= 4) word = few;
  }
  return `${String(n)} ${word}`;
}

/**
 * The second line of a track row: the billing and the year, whichever exist, joined.
 *
 * Nothing is drawn for what is missing — no «Unknown Artist», no «—», no dangling separator.
 * A track with neither has no second line at all, and the row says only its title.
 */
export function trackByline(performer: string, year: number | null): string {
  const parts: string[] = [];
  if (performer.trim() !== '') parts.push(performer);
  if (year !== null) parts.push(String(year));
  return parts.join(' · ');
}
